import { type AuthProvider } from '@refinedev/core';

import axios from 'axios';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

import { axiosAuthInstance, axiosInstance } from '@/utility';
import { getDefaultHeaders } from '@/utility/helper/getDefaultHeaders';
import { LoginVariables } from '@/modules/apps/model/auth';

export interface CurrentUser {
  user_id: string;
  user_name: string;
  email: string;
  tenant_id: string;
  first_name: string;
  last_name: string;
}
export interface AuthToken {
  access_token: string;
  refresh_token: string;
}

const authProvider: AuthProvider = {
  login: async ({ username, password }: LoginVariables) => {
    const response = await axiosAuthInstance.post('api/customer-portal/v1/auth/login', { username, password }).then((response) => response?.data);
    const token = response.data.access_token;

    if (token) {
      const decodedToken = jwtDecode<any>(token);
      Cookies.set('access_token', token);
      const userLogin = {
        exp: decodedToken.exp,
        iat: decodedToken.iat,
        jti: decodedToken.jti,
        iss: decodedToken.iss,
        aud: decodedToken.aud,
        userId: response?.data?.user_id,
        typ: decodedToken.typ,
        azp: decodedToken.azp,
        session_state: decodedToken.session_state,
        acr: decodedToken.acr,
        allowed_origins: decodedToken['allowed-origins'],
        realm_access: decodedToken.realm_access,
        resource_access: decodedToken.resource_access,
        scope: decodedToken.scope,
        sid: decodedToken.sid,
        username: decodedToken.preferred_username,
        firstName: decodedToken.given_name,
        lastName: decodedToken.family_name,
        email: decodedToken.email,
        fullName: response.data.name,
        tenantId: response.data.tenant_id,
        customerId: response.data.customer_id,
      };

      Cookies.set('user_login', JSON.stringify(userLogin));

      const permissions = response.data.user_roles.permissions.map((p: any) => {
        const {
          relative_path: omit1,
          method: omit2,
          menu_id: omit3,
          ...rest
        } = p;
        return { ...rest };
      });
      const userRoles = { ...response.data.user_roles, permissions };

      Cookies.set('user_roles', JSON.stringify(userRoles));

      axiosInstance.defaults.headers.common = getDefaultHeaders(userLogin);

      return {
        authenticated: true,
        success: true,
        redirectTo: '/',
      };
    }
    return {
      success: false,
      error: { message: 'Login failed', name: 'InvalidCredentials' },
    };
  },
  logout: async () => {
    const allCookies = Cookies.get();
    Object.keys(allCookies).forEach((cookieName) => {
      Cookies.remove(cookieName);
    });

    delete axiosInstance.defaults.headers.common['x-user-id'];
    delete axiosInstance.defaults.headers.common['x-customer-id'];
    delete axiosInstance.defaults.headers.common['x-tenant-id'];

    window.location.replace('/auth/login');

    return {
      success: true,
      redirectTo: '/auth/login',
    };
  },
  onError: async (error: { response: { status: number } }) => {
    if (error?.response?.status === 401) {
      return {
        logout: true,
      };
    }
    const errorMessage = `HTTP Error: ${error}`;
    return {
      error: new Error(errorMessage),
    };
  },
  check: async () => {
    const accessToken = Cookies.get('access_token');

    if (accessToken) {
      axios.defaults.headers.common = {
        Authorization: `Bearer ${accessToken}`,
      };

      return {
        authenticated: true,
      };
    }

    return {
      authenticated: false,
      error: {
        message: 'Check your authentication token',
        name: 'Token Expired',
      },
      redirectTo: '/auth/login',
      logout: true,
    };
  },
  getPermissions: async () => {
    const rolesFromCookies = Cookies.get('user_roles');

    if (rolesFromCookies) {
      const roles = JSON.parse(rolesFromCookies);

      return roles;
    }

    return null;
  },
  forgotPassword: async (payload) => {
    const response = await axiosAuthInstance.post(
      'api/customer-portal/v1/auth/reset-password/generate-link',
      payload,
      {
        headers: { 'x-app-id': 'customer_portal' },
      },
    );

    if (!response) {
      return {
        success: false,
        error: { message: 'Invalid credentials', name: 'InvalidCredentials' },
      };
    }

    return {
      success: true,
    };
  },
  updatePassword: async (payload) => {
    const response = await axiosAuthInstance.post(
      'api/customer-portal/v1/auth/reset-password',
      payload,
      {
        headers: { 'x-app-id': 'customer_portal' },
      },
    );

    if (!response) {
      return {
        success: false,
        error: { message: 'Invalid credentials', name: 'InvalidCredentials' },
      };
    }

    return {
      success: true,
    };
  },
};

export default authProvider;
